// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { OffsetOptions, Placement, ShiftOptions } from "@floating-ui/react";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import FloatingTooltip, { FloatingTooltipStyles } from "@stoneage/App/Components/FloatingTooltip/FloatingTooltip";
import { mergeWithCss } from "@stoneage/XDL/Utilities";
import * as React from "react";
import { FC } from "react";
import { withTheme } from "styled-components";

// XNG-Changes: All OOTB are cleaned up we're currently using Floating UI

export interface TooltipPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS strings or styled-components functions to be injected into nested components. These will override the theme defaults.
     * @themable */
    cssOverrides?: {
        tooltipClickable?: StyledProp<TooltipProps>;
        tooltipBody?: StyledProp<TooltipProps>;
        tooltipContainer?: StyledProp<TooltipProps>;
        tooltipWrapper?: StyledProp<TooltipProps>;
        tooltipArrow?: StyledProp<TooltipProps>;
    };
    /** Props to be passed into the trigger Icon component if no triggerComponent is provided.
     * @themable */
    iconProps?: IconPresentationProps;
    /** Props that will be passed to the typography component rendered in the tooltip.
     * @themable */
    typographyProps?: TypographyPresentationProps;
    // XNG-Changes
    extendedStyles?: FloatingTooltipStyles;
}

export type TooltipComponentProps = MobiusStyledComponentProps<
    "span",
    {
        /** Component to be used as the trigger to optionally replace the default trigger icon. */
        triggerComponent?: React.ReactElement<{ tabIndex: number }>;
        /** The text that will appear in the tooltip */
        text?: string | React.ReactElement | React.ReactNode[];
        // /** Function called on tooltip open */
        // onOpen?: React.EventHandler<React.SyntheticEvent>;
        // /** Function called on tooltip close */
        // onClose?: React.EventHandler<React.SyntheticEvent>;
        /** Text to be used to describe the tooltip trigger. Used for screenreaders. Defaults to "more info" */
        triggerAltText?: string;
        /** Props that will be passed to the tooltip to determin how to pass the text of the tooltip
         * @themable */
        passAsChildren?: boolean;

        trigger?: "hover" | "click" | "manual";
        placement?: Placement;
        referenceId?: string;
        shift?: ShiftOptions;
        offset?: OffsetOptions;
        // XNG-Changes
        /** When true, closes any other open tooltips when this one opens. */
        closeOthersOnOpen?: boolean;
    }
>;

export type TooltipProps = TooltipComponentProps & TooltipPresentationProps;

/**
 * Component that provides supplementatal information on click or enter.
 */
const Tooltip: FC<TooltipProps> = props => {
    const {
        text,
        triggerComponent,
        triggerAltText,
        trigger = "hover",
        placement,
        referenceId,
        mergeCss,
        children,
        passAsChildren,
        shift,
        offset = { mainAxis: 20 },
        cssOverrides,
        closeOthersOnOpen,
    } = props;
    const { spreadProps, applyStyledProp } = applyPropBuilder(props, { component: "tooltip" });

    const [xExtendedStyles] = React.useState(() =>
        mergeWithCss(spreadProps("extendedStyles"), {
            tooltipContent: {
                css: cssOverrides?.tooltipContainer,
            },
            triggerWrapper: { css: cssOverrides?.tooltipWrapper },
        }),
    );

    if (
        (passAsChildren && !children) ||
        (!passAsChildren && (!text || (typeof text === "string" && text.length === 0)))
    ) {
        return null;
    }

    let triggerElement;
    if (triggerComponent) {
        /** remove tabability because of `Clickable` wrapper to remove redundant tab if the triggerComponent
         * is a tabbable component. */
        triggerElement = React.cloneElement(triggerComponent, { tabIndex: -1 });
    } else {
        triggerElement = (
            <IconMemo
                id={referenceId}
                {...spreadProps("iconProps")}
                css={applyStyledProp(["iconProps", "css"], mergeCss)}
            />
        );
    }

    return (
        <FloatingTooltip
            trigger={trigger}
            overlay={false}
            triggerComponent={
                <span>
                    {triggerElement}
                    <VisuallyHidden>{triggerAltText || props.theme!.translate("more info")}</VisuallyHidden>
                </span>
            }
            extendedStyles={xExtendedStyles}
            placement={placement}
            referenceId={referenceId}
            shift={shift}
            offset={offset}
            closeOthersOnOpen={closeOthersOnOpen}
        >
            <div aria-live="polite" data-test-selector="tooltip_TooltipInfo" className="tooltipInfo">
                {passAsChildren ? (
                    <>{children}</>
                ) : (
                    <Typography as="p" {...spreadProps("typographyProps")} data-test-selector="toolTip">
                        {text}
                    </Typography>
                )}
            </div>
        </FloatingTooltip>
    );
};

/** @component */
export default withTheme(Tooltip);
